<template>
  <td :class="classes">
    <SfLink
      v-if="hasLink"
      class="sf-inline"
      :href="href"
      :target="linkTarget"
      data-testid="entries-table-cell-link"
      @click="onClick"
    >
      <i v-if="icon" :class="icon"></i>
      {{ text }}
    </SfLink>
    <template v-else>
      {{ text }}
    </template>
  </td>
</template>

<script setup lang="ts">
import type { TableEntry } from '@app/types'

import { computed, inject } from 'vue'

import SfLink from '@app/shared/vue/components/sf-link.vue'
import { useDrawer } from '@app/shared/pinia/drawer'
import { RightDrawerContent } from '@app/shared/types/drawer'
import type { Attachment } from '@app/application/sidebar-file-attachments/types'
import { DocumentLinkType } from '@app/shared/types/sf-data'
import { useSidebarFilePreviewStore } from '@app/application/sidebar-file-preview/store/sidebar-file-preview'
import { isUsingTwoDrawerLayout } from '@app/utils/domHelpers'

const drawerStore = useDrawer()
import { documentPreviewHandlerKey } from '@app/application/report/injection-keys'
const documentPreviewHandler = inject(documentPreviewHandlerKey, null)
const sidebarFilePreviewStore = useSidebarFilePreviewStore()

interface Props {
  entry: TableEntry
  header: string
  sidebarPreview?: boolean
}

const props = withDefaults(defineProps<Props>(), {})

const classes = computed(() => {
  return props.header === 'value' || hasJournalLink.value ? 'sf-alignr' : ''
})

const hasAdjustmentLink = computed(() => {
  return props.entry.link_to_adjustment && props.header === 'value'
})

const hasDocumentLink = computed(() => {
  return (
    props.header === 'document_reference' && !!props.entry.document_reference
  )
})

const hasJournalLink = computed(() => {
  return props.header === 'journal' && !!props.entry.journal_link
})

const hasLink = computed(() => {
  return (
    hasAdjustmentLink.value || hasDocumentLink.value || hasJournalLink.value
  )
})

const href = computed(() => {
  if (hasAdjustmentLink.value) {
    return props.entry.link_to_adjustment
  } else if (hasDocumentLink.value) {
    return window.Silverfin.Routes.document_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      props.entry['document_reference'],
      { detached: 1 }
    )
  } else if (hasJournalLink.value) {
    return '#'
  } else {
    return ''
  }
})

const journalLink = computed(() => {
  return props.entry.journal_link
})

const icon = computed(() => {
  return hasDocumentLink.value ? 'fa-solid fa-file-pdf' : ''
})

const linkTarget = computed(() => {
  return hasDocumentLink.value || hasAdjustmentLink.value ? '_blank' : '_self'
})

const text = computed(() => {
  if (hasAdjustmentLink.value) {
    return props.entry[props.header]
  } else if (hasDocumentLink.value) {
    return ''
  } else if (hasJournalLink.value) {
    return '#'
  } else {
    return props.entry[props.header]
  }
})

function onClick(e: Event) {
  if (hasJournalLink.value && journalLink.value) {
    e.preventDefault()
    e.stopPropagation()
    window.modalCitizen.openXhrModal(e, 'journal', journalLink.value, {})
  } else if (
    documentPreviewHandler &&
    hasDocumentLink.value &&
    props.entry.document_properties?.previewable
  ) {
    e.preventDefault()
    e.stopPropagation()
    documentPreviewHandler(
      parseInt(props.entry.document_reference),
      props.entry.document_properties as Record<string, unknown>
    )
  } else if (
    isUsingTwoDrawerLayout() &&
    hasDocumentLink.value &&
    props.sidebarPreview &&
    props.entry.document_properties?.previewable
  ) {
    e.preventDefault()
    e.stopPropagation()

    sidebarFilePreviewStore.setAttachment({
      name: props.entry.document_properties?.name,
      size: props.entry.document_properties?.size,
      previewable: props.entry.document_properties?.previewable,
      document_id: parseInt(props.entry.document_reference),
      linkType: DocumentLinkType.Document,
    } as Attachment)

    drawerStore.openDrawer({
      direction: 'right',
      content: RightDrawerContent.FilePreview,
    })
  }
}
</script>
